import { useMemo } from "react";

import { useUser } from "hooks";
import usePlayTo3DDataPrivateSources from "hooks/usePlayTo3DDataPrivateSources";
import useUserModules from "hooks/useUserModules";

import { GeoCalculationsJob } from "../models/GeoCalculations";
import { StickCalculationsJob } from "../models/StickCalculations";
import {
  isEvaResearchJobEntitled,
  isEvaResearchSource
} from "../utils/evaResearchEntitlement";

/**
 * Hook that filters geo calculation jobs based on user access to the model sources.
 * Only returns jobs where the user has access to the associated geo model source.
 * Blank-source (EVA Research) jobs additionally require the Geology or 3D Geo Model tier.
 *
 * Access comes from `/sources/private`, which the server scopes to the caller, so
 * every-role surfaces may use this. Never swap in the admin access list: it is the
 * whole org's private models and their users, with no server role check.
 *
 * @param enabled handed to the sources query, for a caller that may render with
 * nothing to filter and should not ask for the list then.
 */
const useFilteredGeoCalculationsJobs = (
  jobs: GeoCalculationsJob[] | StickCalculationsJob[] | undefined,
  enabled = true
) => {
  const userState = useUser();
  const { hasGeology, has3dGeoModel, geoPlays } = useUserModules();
  const { data: accessibleSources, isLoading } = usePlayTo3DDataPrivateSources(enabled);

  const filteredJobs = useMemo(() => {
    if (!jobs) {
      return jobs;
    }

    // Fail closed: with no resolved access list or no current user there is
    // nothing to check a job against, so withhold every job. A failed lookup
    // leaves no sources, which hides every named-source job the same way.
    if (isLoading || !userState?.user) {
      return [];
    }

    const accessible = new Set((accessibleSources ?? []).map((s) => s.source));
    return (jobs as Array<GeoCalculationsJob | StickCalculationsJob>).filter((job) => {
      if (isEvaResearchSource(job.modelSource)) {
        // EVA-148: hide, don't delete, EVA Research jobs once the subscription lapses.
        return isEvaResearchJobEntitled(job, { hasGeology, has3dGeoModel, geoPlays });
      }
      return accessible.has(job.modelSource);
    });
  }, [
    jobs,
    accessibleSources,
    isLoading,
    userState,
    hasGeology,
    has3dGeoModel,
    geoPlays
  ]);

  return filteredJobs;
};

export default useFilteredGeoCalculationsJobs;
